<template>
  <div v-if="_service" class="w-full px-6 py-4">
    <div class="flex space-x-4 mb-6">
      <o-field-group class="w-full" :label="$t('general.title')" rules="noCurrencyOrBrackets">
        <text-field v-model="_service.title" :label="$t('general.title')" />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        v-if="!createCategory"
        class="w-full"
        :label="$t('general.category')"
      >
        <select-field v-model="_category" :options="serviceCategoryOptions" />

        <p class="text-right mt-1">
          <a
            class="text-blue-500 text-sm cursor-pointer hover:underline text-right"
            @click="createCategory = true"
          >
            {{ $t('components.service.edit.details.create') }}
          </a>
        </p>
      </o-field-group>

      <div
        v-if="createCategory"
        class="rounded border bg-gray-50 dark:bg-gray-900 w-full dark:border-gray-800"
      >
        <div class="border-b p-3">
          <label class="text-sm font-medium">
            {{ $t('components.service.edit.details.newCategory') }}
          </label>
          <p class="text-sm opacity-80">
            {{ $t('components.service.edit.details.newCategoryInfo') }}
          </p>
        </div>
        <div class="flex space-x-4 p-3">
          <o-field-group
            v-slot="{ status }"
            rules="required"
            class="w-full"
            :label="$t('components.service.edit.details.categoryTitle')"
            :info="$t('components.service.edit.details.cateogryTitleInfo')"
          >
            <text-field
              v-model="serviceCategory.name"
              :label="$t('general.title')"
              :status="status"
            />
          </o-field-group>
        </div>

        <div class="flex space-x-4 p-3">
          <o-field-group
            class="w-full"
            :label="$t('components.service.edit.details.categoryInformation')"
            :info="$t('service.categoryDetails')"
          >
            <text-area-field
              v-model="serviceCategory.information"
              :label="$t('general.information')"
            />
          </o-field-group>
        </div>

        <p class="text-right p-3 -mt-4">
          <a
            class="text-blue-500 text-sm cursor-pointer hover:underline text-right"
            @click="createCategory = false"
          >
            {{ $t('components.service.edit.details.select') }}
          </a>
        </p>
      </div>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('service.typeLabel')"
      >
        <div class="w-full border rounded-md overflow-hidden divide-y">
          <radio-field
            v-model="_service.customerType"
            :label="$t('service.type.single')"
            :info="$t('service.type.singleInfo')"
            :value="ServiceCustomerType.single"
            label-class="w-full ml-2 opacity-90 flex justify-between items-center"
            option-class="p-2 flex items-center transition duration-200 cursor-pointer"
          />

          <radio-field
            v-model="_service.customerType"
            :label="$t('service.type.class')"
            :info="$t('service.type.classInfo')"
            :value="ServiceCustomerType.group"
            label-class="w-full ml-2 opacity-90 flex justify-between items-center"
            option-class="p-2 flex items-center transition duration-200 cursor-pointer"
          />
        </div>
      </o-field-group>
    </div>

    <div v-if="isServiceCustomerTypeGroup" class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('general.maxCustomers')"
        :info="$t('service.maxCustomersInfo')"
      >
        <number-field v-model="_service.customerMax" />
      </o-field-group>
    </div>

    <div v-if="$features.get('service-addons') && !isServiceCustomerTypeGroup" class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('components.service.edit.details.addOnMode')"
      >
        <!--<select-field v-model="_service.addOnMode" :options="addOnModeOptions" />-->
        <div class="w-full border rounded-md overflow-hidden divide-y">
          <radio-field
            v-model="_service.addOnMode"
            :label="$t('service.association.notAllowed')"
            :info="$t('service.association.notAllowedInfo')"
            :value="ServiceAssociationAddOnMode.notAllowed"
            label-class="w-full ml-2 opacity-90 flex justify-between items-center"
            option-class="p-2 flex items-center transition duration-200 cursor-pointer"
          />

          <radio-field
            v-model="_service.addOnMode"
            :label="$t('service.association.allowed')"
            :info="$t('service.association.allowedInfo')"
            :value="ServiceAssociationAddOnMode.allowed"
            label-class="w-full ml-2 opacity-90 flex justify-between items-center"
            option-class="p-2 flex items-center transition duration-200 cursor-pointer"
          />

          <radio-field
            v-model="_service.addOnMode"
            :label="$t('service.association.only')"
            :info="$t('service.association.onlyInfo')"
            :value="ServiceAssociationAddOnMode.only"
            label-class="w-full ml-2 opacity-90 flex justify-between items-center"
            option-class="p-2 flex items-center transition duration-200 cursor-pointer"
          />
        </div>
      </o-field-group>
    </div>

    <div v-if="!_service.isNewRecord" class="flex space-x-4 mb-6">
      <o-field-group :label="$t('general.image')" :info="$t('product.visibleInLists')">
        <avatar-field :model="_service" @click="handleUpdateImage" />
      </o-field-group>
    </div>

    <div class="flex space-x-4">
      <o-field-group
        class="w-full"
        :label="$t('general.durationMinutes')"
        :info="$t('schedule.durationMinutesInfo')"
      >
        <number-field v-model="_service.duration" :min="0" />
      </o-field-group>
    </div>

    <p class="text-right mt-1 block px-1 mb-4">
      <a
        class="text-blue-600 text-sm hover:text-blue-700 cursor-pointer"
        @click="handleAdvancedTiming"
      >{{ $t('components.service.edit.details.advancedTimingOptions') }}</a>
    </p>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full md:w-1/2"
        :label="$t('general.price')"
        :info="$t('service.priceInfo')"
      >
        <div class="flex items-center">
          <currency-field v-model="_service.price">
            <template #trailing>
              <div class="h-5 border-l" />
              <checkbox-field
                v-model="_service.priceFrom"
                :label="$t('general.from')"
                class="mt-1 mx-2 flex-shrink-0"
                flip
              />
            </template>
          </currency-field>
        </div>

        <p class="text-right mt-1 block px-1 mb-4">
          <o-tooltip
            :text="$t('components.service.edit.details.apply')"
          >
            <a
              class="text-blue-600 text-sm hover:text-blue-700 cursor-pointer"
              @click="handleUpdatePrices"
            >{{ $t('components.service.edit.details.reset') }}</a>
          </o-tooltip>
        </p>
      </o-field-group>

      <o-field-group
        class="w-full md:w-1/2"
        :label="$t('components.service.edit.details.costPrice')"
        :info="$t('components.service.edit.details.costPriceInfo')"
      >
        <currency-field v-model="_service.costPrice" />
      </o-field-group>
    </div>

    <div class="w-full mb-6">
      <div class="text-sm mr-8">
        <p>{{ $t('general.colour') }}</p>
        <p class="opacity-80">
          {{ $t('components.service.edit.details.colourInfo') }}
        </p>
      </div>

      <ServiceColour v-model:service="_service" />
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        class="w-full"
        :label="$t('general.info')"
        :info="$t('service.infoInfo')"
      >
        <text-area-field v-model="_service.information" rows="5" />
      </o-field-group>
    </div>

    <div class="flex space-x-4 mb-6">
      <o-field-group
        v-if="$features.get('loyalty')"
        col
        reverse
        class="w-full"
        :label="$t('general.loyaltyEnabled')"
        :info="$t('components.service.edit.details.loyaltyEnabledInfo')"
      >
        <toggle-field v-model="_service.loyaltyEnabled" />
      </o-field-group>
    </div>

    

    <div v-if="taxOptions.length" class="mt-10">
      <label class="block text-sm font-medium" tabindex="0">
        <span>{{ $t('general.taxes') }}</span>
      </label>
      <p class="block opacity-80 text-sm mb-6">
        {{ $t('components.service.edit.details.taxesInfo') }}
      </p>

      <o-field-group
        v-for="tax in taxOptions"
        :key="tax.idTax"
        v-slot="{ status }"
        :label="tax.name"
        :name="tax.name"
        :info="$t('tax.includeInItemCost', { name: tax.name, amount: tax.amountLabel })"
        class="mb-4 block"
        col
        reverse
      >
        <toggle-field
          v-model="_service.taxIDs"
          :status="status"
          :value="Number(tax.idTax)"
        />
      </o-field-group>
    </div>

    <o-custom-fields :data="_service.customFields">
      <template #default="{ fields }">
        <div
          v-for="field in fields"
          :key="field.props.id"
          class="flex space-x-4 mb-4"
        >
          <o-field-group class="w-full" :label="field.label">
            <component
              :is="field.component"
              v-bind="field.props"
              v-on="field.events"
            />
          </o-field-group>
        </div>
      </template>
    </o-custom-fields>

    <UpdateImageDialog
      ref="updateImageDialog"
      :service="_service"
      @update="handleUpdateImageSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import ServiceColour from '@/components/service/elements/Colour.vue'
import { OCustomFields, OAvatar } from '@/components/elements'
import UpdateImageDialog from '@/components/service/actions/ServiceImageDialog.vue'
import {
  TextField,
  CurrencyField,
  SelectField,
  NumberField,
  TextAreaField,
  ToggleField,
  SegmentField,
  CheckboxField,
  ColourField,
  AvatarField,
  RadioField,
} from '@/components/fields'

import { ServiceCategory } from '~/_api/models/serviceCategory'
import { ServiceCustomerType, ServiceAddOnMode } from '~/_api/models/service'
import type { Service } from '~/_api/models/service'

const props = defineProps<{
  service: Service
}>()

const emit = defineEmits<{
  (e: 'update:service', service: Service): void
  (e: 'selectTab', tab: string): void
  (e: 'updatePrices'): void
  (e: 'updateServiceCategory', category: ServiceCategory | null): void
  (e: 'updateImageSuccess'): void
}>()

const { $api, $i18n, $bus } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

const updateImageDialog = ref()
const createCategory = ref(false)
const serviceCategory = ref<ServiceCategory | null>(null)
const serviceCategoryDefaults = ref<Record<string, any> | null>(null)

// Expose enum constants for template usage
const ServiceAssociationAddOnMode = ServiceAddOnMode

const _service = computed({
  get: () => props.service,
  set: (value) => emit('update:service', value),
})

const _category = computed({
  get: () => {
    if (_service.value.category) {
      const match = serviceCategoryOptions.value.find((option) => {
        return (
          option.value?.idServiceCategory ===
          _service.value?.category?.idServiceCategory
        )
      })

      if (match) {
        return match.value
      }
    }

    return null
  },
  set: (value) => {
    _service.value.category = value
    _service.value.idServiceCategory = value?.idServiceCategory || 0
  },
})

const serviceCategoryOptions = computed(() => {
  const serviceCategories = _service.value.options?.serviceCategories

  const options = serviceCategories?.map((serviceCategory) => ({
    label: serviceCategory.name,
    value: serviceCategory,
  })) || []

  const noCategory = { label: $t('general.none'), value: null }

  return [noCategory, ...options]
})

const taxOptions = computed(() => {
  return _service.value.options?.taxes || []
})

const isServiceCustomerTypeGroup = computed(() => {
  return props.service?.isServiceCustomerTypeGroup
})

watch(createCategory, (value: boolean) => {
  if (value) {
    serviceCategory.value = new ServiceCategory(serviceCategoryDefaults.value)
  } else {
    serviceCategory.value = null
  }
})

watch(serviceCategory, (value) => {
  emit('updateServiceCategory', value)
})

const handleAdvancedTiming = () => {
  emit('selectTab', 'advanced-timing')
}

const handleUpdatePrices = () => {
  emit('updatePrices')
}

const fetchServiceCategoryDefaults = async () => {
  const response = await $api.serviceCategory.defaults()
  serviceCategoryDefaults.value = response.data?.toJSON() || null
}

const resetCreateCategory = () => {
  createCategory.value = false
}

const handleUpdateImage = () => {
  updateImageDialog.value?.open()
}

const handleUpdateImageSuccess = (service: Service) => {
  _service.value.images = service.images
  _service.value.hasImage = !!service.images
  $bus.emit.updated('Service')
  emit('updateImageSuccess')
}

// Fetch defaults on mount
fetchServiceCategoryDefaults()

defineExpose({
  resetCreateCategory,
})
</script>
